<template>
  <nav class="gds-sidebar">
    <Transition name="gds-mode">
      <!-- ======== CENTER MODE ======== -->
      <div v-if="mode === 'center'" key="center" class="gds-mode-content">
        <!-- Center name header -->
        <div class="gds-header">
          <span class="gds-header-label">{{ activeCenterName }}</span>
        </div>

        <!-- Scrollable report tabs -->
        <div class="gds-centers">
          <button
            v-for="(sr, index) in salesReports"
            :key="sr.id"
            class="gds-tab"
            :class="{ 'gds-tab--active': activeSrIndex === index }"
            @click="activeSrIndex = index"
          >
            <span class="gds-tab-label">{{ sr.title }}</span>
          </button>

          <!-- Add report button -->
          <button class="gds-tab gds-tab--add" @click="showCreateSheet = true">
            <PlusIcon class="gds-tab-icon" />
            <span class="gds-tab-label">{{ t('sidebar.add_report') }}</span>
          </button>
        </div>
      </div>

      <!-- ======== DASHBOARD MODE ======== -->
      <div v-else-if="mode === 'dashboard'" key="dashboard" class="gds-mode-content">
        <!-- Vue d'ensemble -->
        <button
          class="gds-tab"
          :class="{ 'gds-tab--active': isDashboardOverview }"
          @click="router.push({ name: 'Dashboard' })"
        >
          <Squares2X2Icon class="gds-tab-icon" />
          <span class="gds-tab-label">{{ t('dashboard.overview') }}</span>
        </button>

        <!-- Separator -->
        <div class="gds-separator" />

        <!-- Report list -->
        <div class="gds-centers">
          <router-link
            v-for="component in dashboardComponents"
            :key="component.id"
            :to="{ name: 'DashboardDetail', params: { reportId: component.id } }"
            custom
            v-slot="{ navigate }"
          >
            <button
              class="gds-tab"
              :class="{ 'gds-tab--active': isDashboardDetail && route.params.reportId === component.id }"
              @click="navigate"
            >
              <span class="gds-tab-label">{{ component.config.title }}</span>
            </button>
          </router-link>

          <!-- Add report -->
          <button class="gds-tab gds-tab--add" @click="dashboardShowCreateSheet = true">
            <PlusIcon class="gds-tab-icon" />
            <span class="gds-tab-label">{{ t('sidebar.add_report') }}</span>
          </button>
        </div>
      </div>
    </Transition>

    <!-- Profile link at bottom -->
    <router-link :to="{ name: 'Profile' }" custom v-slot="{ navigate }">
      <button class="gds-profile" :class="{ 'gds-profile--active': isProfileActive }" @click="navigate">
        <Transition name="gds-profile-swap" mode="out-in">
          <div class="gds-profile-inner" :key="avatarName">
            <UserAvatar :name="avatarName" :color="avatarColor" size="sm" />
            <span class="gds-profile-name">{{ avatarName }}</span>
          </div>
        </Transition>
      </button>
    </router-link>
  </nav>
</template>

<script setup lang="ts">
defineOptions({ name: 'GlobalDesktopSidebar' })

import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  PlusIcon,
  Squares2X2Icon,
} from '@heroicons/vue/24/outline'
import { useUserManagerStore } from '../../stores/userManagerStore'
import { useSalesReportsStore } from '../../stores/salesReports'
import { useDesktopReportNav } from '../../composables/useDesktopReportNav'
import { useDashboardViewMode } from '../../composables/useDashboardViewMode'
import UserAvatar from '../UserAvatar.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const userManagerStore = useUserManagerStore()
const salesReportsStore = useSalesReportsStore()
const { activeSrIndex, showCreateSheet } = useDesktopReportNav()

// --- Mode detection ---

const mode = computed<'center' | 'dashboard' | 'other'>(() => {
  if (route.name === 'Center') return 'center'
  if (route.path.startsWith('/dashboard')) return 'dashboard'
  return 'other'
})

// --- Center mode data ---

const salesReports = computed(() => salesReportsStore.salesReports ?? [])

const activeCenterName = computed(() => {
  const centerId = Number(route.params.centerId)
  const center = userManagerStore.displayedCenters.find((c) => c.id === centerId)
  return center?.preferences?.label || center?.title || ''
})

// --- Dashboard mode data ---

const { showCreateSheet: dashboardShowCreateSheet } = useDashboardViewMode()
const dashboardComponents = computed(() => userManagerStore.user?.components ?? [])
const isDashboardOverview = computed(() => route.name === 'Dashboard')
const isDashboardDetail = computed(() => route.name === 'DashboardDetail')

// --- Profile ---

const isProfileActive = computed(() => route.path.startsWith('/profile'))

const activeCenter = computed(() => {
  if (mode.value === 'center') {
    const centerId = Number(route.params.centerId)
    return userManagerStore.user?.centers.find(c => c.id === centerId)
  }
  return userManagerStore.user?.centers[0]
})

const avatarName = computed(() => {
  return activeCenter.value?.connection?.name?.trim() || 'U'
})

const avatarColor = computed(() => {
  return activeCenter.value?.connection?.color || '#06C8FF'
})
</script>

<style scoped>
.gds-sidebar {
  width: 176px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 6px 8px 0 0;
  overflow: hidden;
  position: relative;
}

/* Center name header */
.gds-header {
  padding: 10px 16px 8px;
}

.gds-header-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted-foreground);
  opacity: 0.65;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.gds-tab {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 14px 12px 16px;
  border-radius: 0 10px 10px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s var(--ease-standard);
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  position: relative;
  width: 100%;
  text-align: left;
}

.gds-tab-icon {
  width: 17px;
  height: 17px;
  stroke-width: 1.9;
  color: var(--muted-foreground);
  opacity: 0.7;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.gds-tab-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--muted-foreground);
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
  transition: color 0.2s ease;
}

/* Hover — text/icon lift */
.gds-tab:hover:not(.gds-tab--active) .gds-tab-icon,
.gds-tab:hover:not(.gds-tab--active) .gds-tab-label {
  color: var(--foreground);
  opacity: 1;
}

/* Active state — confident but not loud */
.gds-tab--active .gds-tab-icon {
  color: var(--apex-cyan);
  opacity: 1;
}

.gds-tab--active .gds-tab-label {
  color: var(--foreground);
  font-weight: 700;
}

/* Background fill — shared layer for hover + active */
.gds-tab::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: var(--surface-hover);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s var(--ease-standard),
              background 0.25s ease;
  pointer-events: none;
}

:is(.dark *).gds-tab::before {
  background: var(--surface-hover);
}

/* Hover — fill with neutral tint */
.gds-tab:hover::before {
  transform: scaleX(1);
}

/* Active — fill with brand tint (overrides hover color) */
.gds-tab--active::before {
  transform: scaleX(1);
  background: rgba(6, 200, 255, 0.05);
}

:is(.dark *).gds-tab--active::before {
  background: rgba(6, 200, 255, 0.07);
}

/* Active indicator — left edge bar */
.gds-tab::after {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 2.5px;
  border-radius: 0 2px 2px 0;
  background: var(--apex-cyan);
  box-shadow: 0 0 8px rgba(6, 200, 255, 0.4);
  transform: scaleY(0);
  transform-origin: center;
  transition: transform 0.3s var(--ease-standard);
  pointer-events: none;
}

.gds-tab--active::after {
  transform: scaleY(1);
}

:is(.dark *).gds-tab--active::after {
  box-shadow: 0 0 12px rgba(6, 200, 255, 0.3);
}

/* Add report button — muted accent styling */
.gds-tab--add .gds-tab-icon {
  color: var(--apex-cyan);
  opacity: 0.6;
}

.gds-tab--add .gds-tab-label {
  color: var(--muted-foreground);
  opacity: 0.7;
  font-size: 12px;
}

.gds-tab--add:hover .gds-tab-icon {
  opacity: 1;
}

.gds-tab--add:hover .gds-tab-label {
  opacity: 1;
  color: var(--foreground);
}

/* No left-edge indicator or brand fill for the add button */
.gds-tab--add::after {
  display: none;
}

/* Separator between overview and report list */
.gds-separator {
  height: 1px;
  margin: 6px -8px 6px 0;
  background: var(--border-subtle);
}

/* Toggle button for grid/table view mode */
.gds-toggle-btn {
  margin-left: auto;
  padding: 4px;
  border-radius: 6px;
  color: var(--muted-foreground);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
}

.gds-toggle-btn:hover {
  color: var(--foreground);
  background: rgba(0, 0, 0, 0.05);
}

:is(.dark *).gds-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Mode content wrapper — fills available space above profile */
.gds-mode-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Mode crossfade — simultaneous overlap, synced with page transition */
.gds-mode-enter-active,
.gds-mode-leave-active {
  transition: opacity 250ms ease-out;
}
.gds-mode-leave-active {
  position: absolute;
  inset: 0;
  bottom: auto;
}
.gds-mode-enter-from { opacity: 0; }
.gds-mode-leave-to   { opacity: 0; }

/* Centers list — scrollable (reused for report tabs) */
.gds-centers {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
  scrollbar-width: thin;
}

/* Profile link at bottom */
.gds-profile {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid var(--border-subtle);
  background: transparent;
  border-left: none;
  border-right: none;
  border-bottom: none;
  cursor: pointer;
  transition: background 0.15s ease;
  width: 100%;
  text-align: left;
}

.gds-profile:hover {
  background: var(--surface-hover);
}

:is(.dark *).gds-profile:hover {
  background: var(--surface-hover);
}

.gds-profile--active {
  background: rgba(6, 200, 255, 0.05);
}

:is(.dark *).gds-profile--active {
  background: rgba(6, 200, 255, 0.07);
}

.gds-profile-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gds-profile-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
}

/* Profile swap transition */
.gds-profile-swap-enter-active,
.gds-profile-swap-leave-active {
  transition: opacity 80ms ease-out;
}
.gds-profile-swap-enter-from { opacity: 0; }
.gds-profile-swap-leave-to   { opacity: 0; }
</style>
